import { axiosClientv1 } from "./apiClient";
import { ApiResponse } from "../Types";

export interface WebInfoData {
  milestones?: Array<{
    year: string;
    title: string;
    description: string;
  }>;
  values?: Array<{
    icon: string;
    title: string;
    description: string;
  }>;
  team?: Array<{
    name: string;
    role: string;
    bio: string;
  }>;
  contact_info?: Array<{
    icon: string;
    title: string;
    value: string;
    href: string;
    description: string;
  }>;
  faqs?: Array<{
    question: string;
    answer: string;
  }>;
  social_links?: Array<{
    name: string;
    href: string;
    icon: string;
  }>;
  business_hours?: Array<{
    title: string;
    value: string;
    href: string;
    description: string;
  }>;
  hero_content?: Array<{
    hero_title: string;
    hero_subtitle: string;
    button_text: string;
  }>;
  about_us?: Array<{
    about_title: string;
    about_content: string;
    about_image: string;
  }>;
  footer_content?: Array<{
    copyright_text: string;
    footer_description: string;
    footer_email: string;
    footer_phone: string;
  }>;
  web_logo?: string;
  web_favicon?: string;
  background_image?: string;
}

export interface WebInfoResponse {
  success: boolean;
  message: string;
  data: WebInfoData | null;
  section: string;
}

export const GetWebInfo = async (): Promise<ApiResponse<WebInfoData>> => {
  try {
    const response = await axiosClientv1.get(`/web-info`);
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to load web info.",
      data: null,
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const GetPublicWebInfo = async (): Promise<ApiResponse<WebInfoData>> => {
  try {
    const response = await axiosClientv1.get(`/web-info/public`);
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to load public web info.",
      data: null,
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const CreateOrUpdateWebInfo = async (
  data: WebInfoData,
): Promise<ApiResponse<WebInfoData>> => {
  try {
    const response = await axiosClientv1.post(`/web-info`, data);
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to save web info.",
      error: {
        code: error?.response?.status || 400,
        type: "bad_request",
      },
    };
  }
};

export const DeleteWebInfo = async (): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(`/web-info/delete`, {});
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to delete web info.",
      error: {
        code: error?.response?.status || 400,
        type: "bad_request",
      },
    };
  }
};

export const UploadWebLogo = async (
  file: File,
): Promise<ApiResponse<any>> => {
  try {
    const formData = new FormData();
    formData.append("logo", file);
    const response = await axiosClientv1.post(`/web-info/upload-logo`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to upload logo.",
      data: null,
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const UploadWebFavicon = async (
  file: File,
): Promise<ApiResponse<any>> => {
  try {
    const formData = new FormData();
    formData.append("favicon", file);
    const response = await axiosClientv1.post(`/web-info/upload-favicon`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to upload favicon.",
      data: null,
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const UploadBackgroundImage = async (
  file: File,
): Promise<ApiResponse<any>> => {
  try {
    const formData = new FormData();
    formData.append("background_image", file);
    const response = await axiosClientv1.post(`/web-info/upload-background`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to upload background.",
      data: null,
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};
